/**
 * 서비스 이용 계약서 — 운영자가 빈칸(갑 정보·시작일·월 요금·목표 질문)을 채워 저장하고,
 * 화면에서 인쇄·PDF 로 뽑아 서명을 받는다. 계약 문구 자체는 web 의 템플릿 한 곳에 있다.
 *
 * 전부 **운영자 전용**이다 (260915-access-roles).
 */
import { z } from 'zod';
import { BrandKindQuery, IdParams, IsoDateTime, KstDate, Ok } from '../common.ts';
import { defineRoute } from '../route.ts';

export const CONTRACT_STATUSES = ['draft', 'signed'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

/** 별표 2 에 적는 목표 질문 수의 상한 — 계약서가 한 장에 담기는 정도 */
export const CONTRACT_MAX_QUERIES = 30;

export const ContractInput = z.object({
  /** 등록된 고객에서 출발했을 때만. 계약 먼저, 고객 등록은 나중일 수 있다 */
  brandId: z.uuid().nullable(),
  companyName: z.string().trim().min(1).max(120),
  representativeName: z.string().trim().min(1).max(60),
  /** 서명란 — 비워 두고 손으로 쓸 수 있다 */
  businessNumber: z.string().trim().max(20),
  address: z.string().trim().max(200),
  contact: z.string().trim().max(120),
  /** 첫 구독 요금이 결제되는 날 = 계약 시작일 (제3조) */
  startDate: KstDate,
  /** 서명란 위의 날짜 */
  contractDate: KstDate,
  /** 월 구독 요금(원, 부가가치세 별도) */
  monthlyFee: z.number().int().min(0).max(1_000_000_000),
  /** 추가 질문 1개당 월 요금(원, 부가가치세 별도) — 제5조 3항. 0 이면 계약서에 빈칸으로 남는다 */
  extraQueryFee: z.number().int().min(0).max(1_000_000_000),
  queries: z.array(z.string().trim().min(2).max(300)).min(1).max(CONTRACT_MAX_QUERIES),
});
export type ContractInput = z.infer<typeof ContractInput>;

export const Contract = ContractInput.extend({
  id: z.uuid(),
  brandName: z.string().nullable(),
  /** draft = 작성 중 · signed = 서명을 받아 체결됨 */
  status: z.enum(CONTRACT_STATUSES),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Contract = z.infer<typeof Contract>;

export const listContracts = defineRoute({
  method: 'get',
  path: '/api/contracts',
  access: 'admin',
  summary: '작성한 계약서 목록 (최신순)',
  /** 실제 탭은 고객 없는 계약서도 함께 보인다 — 테스트 탭은 테스트 브랜드의 계약서만 */
  query: BrandKindQuery,
  response: z.object({ contracts: z.array(Contract) }),
});

/** 고객을 골랐을 때 화면이 채울 값 — 대표자명·연락처는 자가 가입 계정에만 있다 */
export const contractSource = defineRoute({
  method: 'get',
  path: '/api/contracts/source',
  access: 'admin',
  summary: '고객 하나의 업체명·대표자명·연락처·목표 질문',
  query: z.object({ brandId: z.uuid() }),
  response: z.object({
    companyName: z.string(),
    representativeName: z.string().nullable(),
    contact: z.string().nullable(),
    queries: z.array(z.string()),
  }),
});

export const getContract = defineRoute({
  method: 'get',
  path: '/api/contracts/{id}',
  access: 'admin',
  summary: '계약서 한 건',
  params: IdParams,
  response: z.object({ contract: Contract }),
});

export const createContract = defineRoute({
  method: 'post',
  path: '/api/contracts',
  access: 'admin',
  summary: '계약서 저장(작성 중)',
  body: ContractInput,
  response: z.object({ contract: Contract }),
});

export const updateContract = defineRoute({
  method: 'patch',
  path: '/api/contracts/{id}',
  access: 'admin',
  summary: '계약서 수정 · 체결 표시',
  params: IdParams,
  body: ContractInput.extend({ status: z.enum(CONTRACT_STATUSES) }),
  response: z.object({ contract: Contract }),
});

export const deleteContract = defineRoute({
  method: 'delete',
  path: '/api/contracts/{id}',
  access: 'admin',
  summary: '계약서 삭제',
  params: IdParams,
  response: Ok,
});
